import { App } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { z } from 'zod'
import { apiFetch } from './api'
import { authClient } from './auth-client'
import { log } from './log'
import { apiUrl, isNative, nativeDebug } from './platform'
import { tokenStore } from './token-store'

const CALLBACK_PREFIX = 'com.beyonityhello.hugo://auth'
const DEBUG_PREFIX = 'com.beyonityhello.hugo://debug/'
const STATE_KEY = 'hugo.native.state'

type DebugHandlers = { navigate: (to: string) => void }
let debugHandlers: DebugHandlers | null = null

/** Local debug builds only: lets simulator automation drive the app via deep links. */
export function registerDebugHandlers(handlers: DebugHandlers) {
  debugHandlers = handlers
}

async function handleDebugLink(url: URL) {
  if (!nativeDebug) return
  const action = url.pathname.replace(/^\/+/, '') || url.host
  if (url.href.startsWith(`${DEBUG_PREFIX}sign-in`)) {
    // Optional test scenario (`…/debug/sign-in?scenario=triage`); the server rejects unknown ones.
    const scenario = url.searchParams.get('scenario') ?? 'empty'
    await startNativeSignIn('debug', /^[a-z][a-z0-9-]{0,39}$/.test(scenario) ? scenario : 'empty')
    return
  }
  if (url.href.startsWith(`${DEBUG_PREFIX}navigate`)) {
    const to = url.searchParams.get('to') ?? '/'
    if (to.startsWith('/') && !to.startsWith('//')) debugHandlers?.navigate(to)
    return
  }
  if (url.href.startsWith(`${DEBUG_PREFIX}ping`)) {
    // The ping comes back over the live stream: proves the app receives live updates.
    await apiFetch('/api/e2e/ping', z.object({ seq: z.number() }), {
      method: 'POST',
      json: { message: url.searchParams.get('message') ?? 'Simulator ping' },
    }).catch((error: unknown) => log.error('debug ping failed', String(error)))
    return
  }
  log.warn(`unknown debug link: ${action}`)
}

export type NativeSignInResult = { ok: true } | { ok: false; error: string }

type Listener = (result: NativeSignInResult) => void
const listeners = new Set<Listener>()

/** Subscribe to the outcome of the system-browser sign-in. */
export function onNativeSignIn(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function emit(result: NativeSignInResult) {
  for (const listener of listeners) listener(result)
}

/**
 * Open the system browser for sign-in. `entry` is the start path: Google in real builds, the
 * test-only scenario login in local debug builds.
 */
export async function startNativeSignIn(
  entry: 'google' | 'debug',
  scenario = 'empty',
): Promise<void> {
  const state = crypto.randomUUID()
  sessionStorage.setItem(STATE_KEY, state)
  const debugParams = new URLSearchParams({ scenario, name: 'Simulator Tester', state })
  const url =
    entry === 'google'
      ? apiUrl(`/api/auth/native/start?state=${state}`)
      : apiUrl(`/api/e2e/native-login?${debugParams.toString()}`)
  await Browser.open({ url, presentationStyle: 'popover' })
}

let lastUrl: string | null = null

async function handleCallback(raw: string) {
  if (raw === lastUrl) return
  if (raw.startsWith(DEBUG_PREFIX)) {
    lastUrl = raw
    await handleDebugLink(new URL(raw))
    return
  }
  if (!raw.startsWith(CALLBACK_PREFIX)) return
  lastUrl = raw
  await Browser.close().catch(() => {})
  const url = new URL(raw)
  const expected = sessionStorage.getItem(STATE_KEY)
  sessionStorage.removeItem(STATE_KEY)
  if (!expected || url.searchParams.get('state') !== expected) {
    log.warn('native sign-in: state mismatch, ignoring callback')
    emit({ ok: false, error: 'state_mismatch' })
    return
  }
  const error = url.searchParams.get('error')
  const ott = url.searchParams.get('ott')
  if (error || !ott) {
    emit({ ok: false, error: error ?? 'missing_token' })
    return
  }
  const { error: verifyError } = await authClient.oneTimeToken.verify({ token: ott })
  if (verifyError || !(await tokenStore.get())) {
    log.error('native sign-in: token exchange failed')
    emit({ ok: false, error: 'exchange_failed' })
    return
  }
  emit({ ok: true })
}

/** Register the deep-link handler once at startup (also handles a cold start via the link). */
export async function initNativeAuth() {
  if (!isNative) return
  await App.addListener('appUrlOpen', (event) => {
    void handleCallback(event.url)
  })
  const launch = await App.getLaunchUrl()
  if (launch?.url) await handleCallback(launch.url)
}
